import {
  lazy,
} from 'react';
import {
  Entity,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  definePanelRegistry,
  idParamUrl,
} from 'pages/Dashboard/contexts/sidePanel/panelUrl';
import {
  EventPanelInfo,
  eventPanelUrl,
  getEventPanelIdentity,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/eventPanelUrl';
import {
  getPCCPanelIdentity,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/pccPanelUrl';
import {
  chargesPanelUrl,
  getChargesPanelIdentity,
} from 'pages/Dashboard/pages/Billing/Charges/chargesPanelInfo';
import type {
  FormWorkflowPanelInfo,
} from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/FormWorkflowDetailPanel';
import type {
  TodoPanelInfo,
} from 'pages/Dashboard/pages/Todos/DetailedToDo';
import type {
  StatementHistoryPanelInfo,
} from 'pages/Dashboard/pages/Ledger/StatementHistory';
import {
  getRunPanelIdentity,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';

// Panels load on first open, so the layout doesn't pull in every page's panel
const AppointmentDetails = lazy(() => import('pages/Dashboard/pages/Appointments/pages/List/components/Details'));
const PCCSidePanel = lazy(() => import('pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/PCCSidePanel'));
const ChargesSidePanel = lazy(() => import('pages/Dashboard/pages/Billing/Charges/ChargesSidePanel'));
const FormWorkflowDetailPanel = lazy(() => import('pages/Dashboard/pages/Forms/pages/FormWorkflow/FormWorkflowDetailPanel'));
const DetailedView = lazy(() => import('pages/Dashboard/pages/Todos/DetailedToDo'));
const StatementHistory = lazy(() => import('pages/Dashboard/pages/Ledger/StatementHistory'));
const FaxDetails = lazy(() => import('pages/Dashboard/pages/Fax/Overview/FaxDetails'));
const PCCActivationSettingsPanel = lazy(() => import('pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCActivationSettingsPanel'));
const BulkClaimsPanel = lazy(() => import('pages/Dashboard/pages/Billing/Charges/BulkClaimsPanel'));
const MasterTemplate = lazy(() => import('views/EMR/Settings/VisitNotes/MasterTemplate'));
const SectionTemplate = lazy(() => import('views/EMR/Settings/VisitNotes/SectionTemplate'));
const AuthorizationDetails = lazy(() => import('views/EMR/components/Authorizations/Details'));
const RunOutputPanel = lazy(() => import('views/EMR/components/Panel/RunOutputPanel'));

const getEntityIdentity = ({ id }: Entity) => String(id);

export default definePanelRegistry({
  todo: {
    Component: DetailedView,
    getPanelIdentity: ({ todoId }: TodoPanelInfo) => String(todoId),
    getRecordName: () => 'to-do',
    url: idParamUrl('todoId'),
  },
  formWorkflow: {
    Component: FormWorkflowDetailPanel,
    getPanelIdentity: ({ formWorkflowId }: FormWorkflowPanelInfo) => String(formWorkflowId),
    getRecordName: () => 'form workflow',
    url: idParamUrl('formWorkflowId'),
  },
  // Only appointments are linkable, see eventPanelUrl
  event: {
    Component: AppointmentDetails,
    getPanelIdentity: getEventPanelIdentity,
    getRecordName: ({ kind }: EventPanelInfo) => (kind === 'appointment' ? 'appointment' : 'event'),
    url: eventPanelUrl,
  },
  charges: {
    Component: ChargesSidePanel,
    getPanelIdentity: getChargesPanelIdentity,
    getRecordName: () => 'charge',
    url: chargesPanelUrl,
  },
  // Not linkable: these keep rich in-memory props and add no URL params
  pcc: {
    Component: PCCSidePanel,
    getPanelIdentity: getPCCPanelIdentity,
  },
  statementHistory: {
    Component: StatementHistory,
    getPanelIdentity: ({ patientId }: StatementHistoryPanelInfo) => String(patientId),
  },
  fax: {
    Component: FaxDetails,
    getPanelIdentity: getEntityIdentity,
  },
  masterTemplate: {
    Component: MasterTemplate,
    getPanelIdentity: getEntityIdentity,
  },
  sectionTemplate: {
    Component: SectionTemplate,
    getPanelIdentity: getEntityIdentity,
  },
  authorization: {
    Component: AuthorizationDetails,
    getPanelIdentity: getEntityIdentity,
  },
  pccActivationSettings: {
    Component: PCCActivationSettingsPanel,
    getPanelIdentity: () => 'pccActivationSettings',
  },
  bulkClaims: {
    Component: BulkClaimsPanel,
    getPanelIdentity: () => 'bulkClaims',
  },
  runOutput: {
    Component: RunOutputPanel,
    getPanelIdentity: getRunPanelIdentity,
  },
});
